import { Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { ConsolidatedChart } from 'layouts/dash/types';
import questions from 'pages/Questions/Area/QuestionPsychologicalSafety/questions.json';
import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import api from 'services/api';
import { useLoadingStore } from 'stores/loadingStore';

export default function SegurancaPsicologicaConsolidated(props: ConsolidatedChart) {
  const { evaluations } = props;

  const setLoading = useLoadingStore((state) => state.setLoading);

  const [info, setInfo] = useState<{
    bars: number[];
    open: string[];
    count: number;
    names: string[];
  } | null>();

  const average = (info?.bars?.reduce((acc, cur) => acc + cur, 0) ?? 0) / 7 || 0;

  useEffect(() => {
    setLoading(true);
    api
      .post(`/tableresponse/evaluations`, { evaluations: evaluations })
      .then(({ data }) => setInfo(data))
      .catch(() => toast.error('Erro ao buscar dados'))
      .finally(() => setLoading(false));
  }, [evaluations]);

  if (!info) return null;

  return (
    <Flex w="100%" h="100%" justifyContent="center" alignItems="center" gap="2rem">
      <Flex maxWidth="40.5rem" width="100%" height="24rem" flexDirection="column">
        <Bar
          data={{
            labels: [{ result: '' }, ...questions[0].questions].map(({ result }, index) =>
              index > 0 ? (`${index}. ` + result).match(/(.{1,35})(?:\s|$)/g)?.map((chunk) => chunk.trim()) : '',
            ),
            // labels: [...questions[0].questions].map(({ result }, index) =>
            //   index >= 0 ? (`${index + 1}. ` + result).match(/(.{1,35})(?:\s|$)/g)?.map((chunk) => chunk.trim()) : '',
            // ),
            datasets: [
              {
                data: [0, ...info.bars],
                // data: [0.25,1,2,3,4,5,0],
                // data: info.bars,
                backgroundColor: [0.1, ...info.bars].map((it) =>
                  it <= 0.5 ? 'transparent' : it >= 4 ? '#00d26a' : it >= 3 ? '#fcd53f' : '#f8312f',
                ),
                barThickness: 25,
              },
            ],
          }}
          options={{
            animation: {
              duration: 0,
            },
            responsive: true,
            indexAxis: 'y',
            layout: {
              padding: {},
            },
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
                position: 'bottom',
              },
              datalabels: {
                formatter: (v) => (v !== 0 ? v.toFixed(1).replace('.', ',') : ''),
                align: (ctx) => {
                  const index = ctx.dataIndex;
                  const value = ctx.dataset.data[index];
                  if (!value || typeof value !== 'number') return 'end';
                  return value >= 0.5 ? 'start' : 'end';
                },
                anchor: 'end',
                labels: {
                  title: {
                    clamp: true,
                    color: 'black',
                    font: {
                      weight: 800,
                      size: 13,
                    },
                  },
                },
              },
              annotation: {
                annotations: {
                  line1: {
                    type: 'line',
                    borderDash: [6, 6],
                    borderColor: '#4242ff',
                    borderWidth: 2,
                    scaleID: 'x',
                    value: average,
                    drawTime: 'beforeDatasetsDraw',
                    label: {
                      backgroundColor: 'transparent',
                      color: 'black',
                      font: {
                        size: 12,
                        weight: '800',
                      },
                      content: `Média geral: ${average.toFixed(1).replace('.', ',')}`,
                      display: true,
                      position: 'start',
                      xAdjust: () => (average > 3.5 ? -60 : 60),
                    },
                  },
                },
              },
            },
            scales: {
              y: {
                ticks: {
                  color: 'black',
                  font: {
                    size: 11,
                    weight: 'bold',
                  },
                  crossAlign: 'near',
                },
                grid: {
                  borderWidth: 1,
                  display: false,
                },
              },
              x: {
                suggestedMin: 0,
                suggestedMax: 5,
                title: {
                  display: true,
                  text: 'Média de equipe (1 a 5)',
                },
                grid: {
                  borderWidth: 1,
                  display: false,
                },
                ticks: {
                  stepSize: 0.5,
                },
              },
            },
          }}
        />
        <Flex alignItems="center" m="0.3125rem" width="100%" justifyContent="center">
          {/* '#00d26a' : it >= 3 ? '#fcd53f' : '#f8312f' */}
          <Text bg={'#f8312f'} borderRadius="50%" w="1rem" h="1rem" mr="0.3125rem" />
          <Text fontSize="0.75rem" mr="1rem">
            1,0 - 2,9 = Baixo
          </Text>
          <Text bg={'#fcd53f'} borderRadius="50%" w="1rem" h="1rem" mr="0.3125rem" />
          <Text fontSize="0.75rem" mr="1rem">
            3,0 - 3,9 = Moderado
          </Text>
          <Text bg={'#00d26a'} borderRadius="50%" w="1rem" h="1rem" mr="0.3125rem" />
          <Text fontSize="0.75rem" mr="0.625rem">
            4,0 - 5,0 = Alto
          </Text>
        </Flex>
        <Text fontWeight="800" align="center" marginTop="0.625rem" fontSize="1.25rem">
          Percepção de Segurança Psicológica
        </Text>
        <Text
          as="i"
          fontWeight="400"
          align="center"
          marginTop="0.625rem"
          fontSize="0.875rem"
          maxWidth="700"
          color="gray.500"
        >
          As barras representam a média das respostas de todos os respondentes para cada afirmativa.
          <br />A linha tracejada azul indica a média geral das 7 afirmativas.
        </Text>
      </Flex>

      <Flex
        boxShadow={'#7a7a7a 0rem 0rem 0.0625rem'}
        maxWidth="35rem"
        width="100%"
        borderRadius={'0.3125rem'}
        overflowY="auto"
        maxHeight="min(37.5rem, 100%)"
        alignSelf="flex-start"
        marginTop="auto"
        marginBottom="auto"
        style={{ scrollbarWidth: 'thin' }}
      >
        <Table variant="striped">
          <Thead position="sticky" top="0">
            <Tr bgColor={'white'}>
              <Th>Comentários</Th>
            </Tr>
          </Thead>
          <Tbody>
            {info?.open?.map((user, index) => (
              <Tr key={index}>
                <Td>{user}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </Flex>
  );
}
